<template>
  <div class="dialog-body">
    <!-- 使用 v-for 循环渲染各大信息区块 -->
    <div v-for="(section, index) in details" :key="index">
      <div v-show="section.show" class="detail-section">
        <!-- 区块标题 -->
        <div class="section-title">{{ section.title }}</div>

        <!-- Element UI 描述列表组件构建表格，基于屏幕宽度动态控制列数(column) -->
        <el-descriptions
          border
          :column="descColumn"
          class="custom-descriptions"
        >
          <!-- 循环渲染具体的键值对项目 -->
          <el-descriptions-item
            v-for="(item, idx) in section.items"
            :key="idx"
            :label="item.label"
            :span="item.span || 1"
            label-class-name="custom-label"
            content-class-name="custom-content"
          >
            <span :class="['status-tag1', 'status' + item.value]">
              {{ item.value }}
            </span>
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "RoomDetailDialog",
  props: {
    details: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      // 描述列表的列数，用于响应式布局
      descColumn: 2,
    };
  },
  mounted() {
    // 组件挂载时检查一遍屏幕宽度，设置合适的列数
    this.checkWidth();
    // 监听窗口大小变化事件以实现响应式切换功能
    window.addEventListener("resize", this.checkWidth);
  },
  beforeDestroy() {
    // 销毁组件时移除监听器，防内存泄漏
    window.removeEventListener("resize", this.checkWidth);
  },
  methods: {
    /**
     * 根据视口宽度分配 el-descriptions 组件的排版列数
     */
    checkWidth() {
      // 屏幕宽度小于等于 768px（手机或小平板）时，单列显示，否则 2 列显示
      if (window.innerWidth <= 768) {
        this.descColumn = 1;
      } else {
        this.descColumn = 2;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
/* =========== 颜色与全局变量的定义 =========== */
$color-text-main: #333333; /* 主要文字颜色 */
$color-text-label: #8c8c8c; /* 标签(Label)灰字颜色 */
$color-text-success: #67c23a; /* 已出租（成功）绿字颜色 */
$color-bg-white: #ffffff; /* 基础白色背景色 */
$color-border-line: #ebeef5; /* 表格基础边框颜色 */

// 标签样式变量
$color-tag-text: #606266; /* "未缴费" 等标签字体颜色 */
$color-tag-border: #dcdfe6; /* "未缴费" 边框颜色 */
$color-tag-bg: #f4f4f5; /* "未缴费" 背景颜色 */

// 字体与间距
$font-size-title: 15px; /* 模块标题字号 */
$font-size-base: 14px; /* 模块内容基础字号 */
$margin-section: 20px; /* 区块之间间距 */

/* =========== 样式主题表 =========== */
.room-detail-container {
  // 覆盖 element-ui dialog 个别默认样式
  ::v-deep .custom-room-dialog {
    border-radius: 6px;

    .el-dialog__header {
      border-bottom: 1px solid $color-border-line;
      padding: 20px;
      .el-dialog__title {
        font-weight: bold;
        color: $color-text-main;
      }
    }

    .el-dialog__body {
      padding: 10px 20px 0 20px;
      border: 1px solid red;
      max-height: 70vh;
      overflow-y: auto; // 响应式保证高度不足时有滚动条
    }

    .el-dialog__footer {
      border-top: 1px solid $color-border-line;
      padding: 15px 20px;
    }
  }
}

.dialog-body {
  .detail-section {
    margin-bottom: $margin-section;

    // 区块标题样式
    .section-title {
      font-size: $font-size-title;
      font-weight: 600;
      color: $color-text-main;
      margin-bottom: 12px;
      margin-top: 8px;
    }

    // 统一定制 Description 的 Label 与 Content 样式
    ::v-deep .custom-descriptions {
      border-radius: 4px;
      overflow: hidden;

      .el-descriptions-item__label.is-bordered-label {
        background: $color-bg-white !important; // 使标签行背景为白底，一比一复刻
        color: $color-text-label;
        text-align: center;
        width: 120px;
        font-weight: normal;
      }

      .el-descriptions-item__content {
        color: $color-text-main;
        font-size: $font-size-base;
        padding-left: 20px;
      }
    }

    // "已出租" 绿色文本
    .status-success {
      color: $color-text-success;
    }

    // "未缴费" 自定义小标签边框样式
    .status-tag {
      display: inline-block;
      padding: 2px 10px;
      font-size: 12px;
      color: $color-tag-text;
      background-color: $color-tag-bg;
      border: 1px solid $color-tag-border;
      border-radius: 2px;
    }
  }
}

/* ============ 响应式调整 ============ */
@media screen and (max-width: 768px) {
  // 窄屏幕时调整弹窗宽度至近乎满屏
  ::v-deep .custom-room-dialog {
    width: 95% !important;
  }
  .dialog-body {
    .detail-section {
      ::v-deep .custom-descriptions {
        .el-descriptions-item__label.is-bordered-label {
          width: 90px; // 小屏幕缩短表头占位宽度
        }
        .el-descriptions-item__content {
          padding-left: 10px;
        }
      }
    }
  }
}

.left_top {
  border-top-left-radius: 10px;
}

.dialog-body {
  max-height: 800px;
  overflow-x: hidden;
  overflow-y: scroll;
}

::v-deep .detail-section {
  border: 1px solid #e2e8f0;
  padding: 20px;
  border-radius: 10px;
  text-align: left;
}

::v-deep .el-descriptions-item__cell {
  text-align: left !important;
  // border: 1px solid red;
}

::v-deep .rent-control-container .tabs-header {
  text-align: left !important;
  // border: 1px solid red;
}
</style>